import React, { useState, useEffect, useRef } from 'react';
import { MapContainer, TileLayer, CircleMarker, useMapEvent } from 'react-leaflet';
import "leaflet/dist/leaflet.css";
import './ShippingInfo.css';
import { useOrderContext } from '../../../Context/OrderContext';

const ShippingInfo = ({ onContinue, onBack }) => {
    const { formData, updateFormData, setShippingMethod, onShippingFeeChange } = useOrderContext();
    const [selectedPosition, setSelectedPosition] = useState(formData.shippingAddress || { lat: 37.7749, lng: -122.4194 }); // Default to SF
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(formData.shippingMethod || 'standard');
    const [address, setAddress] = useState(formData.address || ""); // Address input for geocoding
    const [isEditingAddress, setIsEditingAddress] = useState(false); // State to manage the address editing modal
    const [tempAddress, setTempAddress] = useState(address); // Temporary address for editing
    const mapRef = useRef();

    // Separate state for each shipping fee
    const [standardFee, setStandardFee] = useState(0);
    const [fastFee, setFastFee] = useState(0);

    // Sync the state with formData when the component mounts
    useEffect(() => {
        if (formData.shippingAddress) {
            setSelectedPosition(formData.shippingAddress);
        }
        if (formData.shippingMethod) {
            setSelectedShippingMethod(formData.shippingMethod);
        }
        if (formData.address) {
            setAddress(formData.address); // Update address if present in formData
        }

        // Automatically calculate shipping fee when the address is loaded
        const initialStandardFee = calculateShippingFee('standard', formData.shippingAddress);
        const initialFastFee = calculateShippingFee('fast', formData.shippingAddress);
        setStandardFee(initialStandardFee);
        setFastFee(initialFastFee);

    }, [formData]);

    useEffect(() => {
        // Calculate the shipping fee based on the new address and selected method
        const updatedStandardFee = calculateShippingFee('standard', selectedPosition);
        const updatedFastFee = calculateShippingFee('fast', selectedPosition);
    
        setStandardFee(updatedStandardFee);
        setFastFee(updatedFastFee);
    
        // Notify parent component with updated fees
        if (onShippingFeeChange) {
            onShippingFeeChange(
                selectedShippingMethod, 
                selectedShippingMethod === 'fast' ? updatedFastFee : updatedStandardFee
            );
        }
    }, [selectedPosition, selectedShippingMethod]); // Recalculate fees if position OR shipping method changes
    
    const handleShippingMethodChange = (method) => {
        setSelectedShippingMethod(method);
        updateFormData({ shippingMethod: method });
    
        // Calculate and update shipping fee for the selected method
        const updatedFee = method === 'fast' ? fastFee : standardFee;
        setShippingMethod(method, updatedFee);
    };
    

    // Calculate the shipping fee based on the address (region-based)
    const calculateShippingFee = (method, position) => {
        const region = getRegionByCoordinates(position); // Determine region based on position
    
        let fee = 0;
    
        // Define rates based on region
        switch (region) {
            case 'Calgary':
                fee = method === 'fast' ? 8 : 0; // Fast delivery: $8, Standard: Free
                break;
    
            case 'Outside Calgary (Canada)':
                fee = method === 'fast' ? 12 : 5; // Different rates for areas outside Calgary, but within Canada
                break;
    
            case 'Outside Canada':
                fee = method === 'fast' ? 25 : 15; // Higher rates for international shipping
                break;
    
            default:
                fee = method === 'fast' ? 20 : 10; // Default fallback rates
                break;
        }
    
        return fee;
    };

    // Dummy function to simulate region determination based on coordinates
    const getRegionByCoordinates = (position) => {
        const { lat, lng } = position;
    
        console.log('Latitude:', lat, 'Longitude:', lng);
    
        // Calgary Region (expand the range)
        if (lat >= 50.8 && lat <= 51.3 && lng >= -114.2 && lng <= -113.5) {
            console.log('Region: Calgary');
            return 'Calgary';
        }
        
        // Outside Calgary (but still within Canada)
        else if (lat >= 49.0 && lat <= 60.0 && lng >= -125.0 && lng <= -60.0) {
            console.log('Region: Outside Calgary (Canada)');
            return 'Outside Calgary (Canada)';
        }
    
        // Outside Canada (anywhere else in the world)
        else if (lat >= -90.0 && lat <= 90.0 && lng >= -180.0 && lng <= 180.0) {
            console.log('Region: Outside Canada');
            return 'Outside Canada';
        }
    
        // Default catch-all (Other region if it doesn't fall into known categories)
        else {
            console.log('Region: Other');
            return 'Other';
        }
    };
    
    // Handle address change and geocoding with OpenStreetMap Nominatim API
    const handleAddressChange = (e) => {
        setTempAddress(e.target.value); // Change the temporary address during editing
    };

    const geocodeAddress = async () => {
        if (!tempAddress) return;

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(tempAddress)}`
            );
            const data = await response.json();

            if (data.length > 0) {
                const newPosition = {
                    lat: data[0].lat,
                    lng: data[0].lon,
                };
                setSelectedPosition(newPosition);
                updateFormData({ shippingAddress: newPosition });

                // Update shipping fee based on new location and selected shipping method
                const newStandardFee = calculateShippingFee('standard', newPosition);
                const newFastFee = calculateShippingFee('fast', newPosition);
                setStandardFee(newStandardFee);
                setFastFee(newFastFee);

                // Move map to new position
                if (mapRef.current) {
                    mapRef.current.setView([newPosition.lat, newPosition.lng], 12);
                }
            } else {
                alert('Address not found. Try again!');
            }
        } catch (error) {
            console.error('Error fetching geocode:', error);
        }
    };

    // Save the new address and close the modal
    const saveAddress = async () => {
        if (!tempAddress) return;

        try {
            const response = await fetch(
                `https://nominatim.openstreetmap.org/search?format=json&q=${encodeURIComponent(tempAddress)}`
            );
            const data = await response.json();

            if (data.length > 0) {
                const newPosition = {
                    lat: data[0].lat,
                    lng: data[0].lon,
                };

                // Set the final address and update form data
                setAddress(tempAddress); 
                updateFormData({ address: tempAddress, shippingAddress: newPosition });

                // Update shipping fee based on new location
                const newStandardFee = calculateShippingFee('standard', newPosition);
                const newFastFee = calculateShippingFee('fast', newPosition);
                setStandardFee(newStandardFee);
                setFastFee(newFastFee);

            // Update the shipping method and fees in the context
            setShippingMethod(selectedShippingMethod, selectedShippingMethod === 'fast' ? newFastFee : newStandardFee);

                // Move map to the new position
                if (mapRef.current) {
                    mapRef.current.setView([newPosition.lat, newPosition.lng], 12);
                }

                // Close the modal
                setIsEditingAddress(false);
            } else {
                alert('Address not found. Try again!');
            }
        } catch (error) {
            console.error('Error fetching geocode:', error);
        }
    };

    // Open the modal for editing the address
    const openAddressEditor = () => {
        setTempAddress(address); // Set the current address as the temp value when editing starts
        setIsEditingAddress(true);
    };

    return (
        <div>
            <h2>Shipping Information</h2>

            <div className="split-screen">
                {/* Left Section: Current Address and Edit Option */}
                <div className="address-input-section">
                    <div className="address-input">
                        <label>Address:</label>
                        <p>{address || "No address entered yet"}</p>
                        <span className="edit-address-link" onClick={openAddressEditor}>
                            Edit Address
                        </span>
                    </div>
                </div>

                {/* Right Section: Map */}
                <div className="map-section">
                    <MapContainer
                        center={selectedPosition}
                        zoom={12}
                        style={{ height: '100%', width: '100%' }}
                        ref={mapRef}
                    >
                        <TileLayer url="https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}.png" />
                        <CircleMarker
                            center={selectedPosition}
                            radius={15}
                            color="blue"
                            fillColor="blue"
                            fillOpacity={0.8}
                        />
                        <MapEventHandler setSelectedPosition={setSelectedPosition} updateFormData={updateFormData} />
                    </MapContainer>
                </div>
            </div>

            {/* Shipping Method Section */}
            <div className="shipping-method-section">
                <h3>Shipping Method</h3>
                <div className="shipping-method-row">
                    {/* Standard Delivery Box */}
                    <div
                        className={`shipping-box ${selectedShippingMethod === 'standard' ? 'selected' : ''}`}
                        onClick={() => handleShippingMethodChange('standard')}
                    >
                        <div className="shipping-option">
                            <input
                                type="radio"
                                id="standard"
                                name="shippingMethod"
                                value="standard"
                                checked={selectedShippingMethod === 'standard'}
                                onChange={() => handleShippingMethodChange('standard')}
                            />
                            <label htmlFor="standard">Standard Delivery</label>
                        </div>
                        <p className="shipping-price">
                            {standardFee === 0 ? 'Free' : `$${standardFee.toFixed(2)}`}
                        </p>
                        <p className="shipping-description">
                            Estimated 14-20 Day Shipping (Duties and taxes may be due upon delivery)
                        </p>
                    </div>

                    {/* Fast Delivery Box */}
                    <div
                        className={`shipping-box ${selectedShippingMethod === 'fast' ? 'selected' : ''}`}
                        onClick={() => handleShippingMethodChange('fast')}
                    >
                        <div className="shipping-option">
                            <input
                                type="radio"
                                id="fast"
                                name="shippingMethod"
                                value="fast"
                                checked={selectedShippingMethod === 'fast'}
                                onChange={() => handleShippingMethodChange('fast')}
                            />
                            <label htmlFor="fast">Fast Delivery</label>
                        </div>
                        <p className="shipping-price">
                            ${fastFee.toFixed(2)}
                        </p>
                        <p className="shipping-description">
                            Estimated 2-5 Days Shipping (Duties and taxes may be due upon delivery)
                        </p>
                    </div>
                </div>
            </div>

            <div className="button-container">
                <button type="button" onClick={onBack} className="back-button">Back</button>
                <button type="button" onClick={onContinue} className="continue-button">Continue to Payment</button>
            </div>

            {/* Modal for Address Editing */}
            {isEditingAddress && (
                <div className="address-modal">
                    <div className="modal-content">
                        <h3>Edit Address</h3>
                        <input
                            type="text"
                            value={tempAddress}
                            onChange={handleAddressChange}
                            placeholder="Enter your address"
                        />
                        <div className="modal-buttons">
                            <button onClick={() => setIsEditingAddress(false)}>Cancel</button>
                            <button onClick={saveAddress}>Save Address</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const MapEventHandler = ({ setSelectedPosition, updateFormData }) => {
    useMapEvent('click', (e) => {
        const newPosition = {
            lat: e.latlng.lat,
            lng: e.latlng.lng,
        };
        setSelectedPosition(newPosition);
        updateFormData({ shippingAddress: newPosition });
    });

    return null;
};

export default ShippingInfo;
